import React, { useState } from "react";
import {
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import FlagIcon from "@mui/icons-material/Flag";
import { AccountBox, Public } from "@mui/icons-material";

import { AmazonViewDialog } from "./utils/AmazonViewDialog";

import { useTranslation } from "../../../hooks/useTranslation";
import { useMarketplaceStore } from "../../../store/useMarketplaceStore";
import { OrderOptimizedListResponse } from "../utils/types";
import { AssignFlag } from "../../../components/flag/components/assign/AssignFlag";
import UnassignFlag from "../../../components/flag/components/assign/UnassignFlag";

type OrderActionsMenuProps = {
  row: OrderOptimizedListResponse;
};

export const OrderActionsMenu: React.FC<OrderActionsMenuProps> = ({ row }) => {
  const selectedCountry = useMarketplaceStore((state) => state.selectedCountry);
  const { translate } = useTranslation("pages.orders.list.fields.actions");

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);
  const [amazonViewDialogOpen, setAmazonViewDialogOpen] = useState(false);

  const openMenu = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => setAnchorEl(null);

  const orderItemsWithImages = row.orderItems.map((item, index) => ({
    id: item.id,
    title: item.title,
    asin: item.asin,
    quantityOrdered: item.quantityOrdered,
    itemPrice: item.itemPrice,
    imageUrl: row.images?.[index],
  }));

  return (
    <>
      <IconButton
        color="primary"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleMenuOpen(e);
        }}
      >
        <MoreVertIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MenuItem
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setAmazonViewDialogOpen(true);
            handleMenuClose();
          }}
        >
          <ListItemIcon>
            <Public />
          </ListItemIcon>
          <ListItemText primary={translate("amazonView.title")} />
        </MenuItem>

        <MenuItem
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            window.open(
              `${selectedCountry?.sellerCentralURL}/orders-v3/order/${row.amazonOrderId}`,
              "_blank",
            );
          }}
        >
          <ListItemIcon>
            <AccountBox />
          </ListItemIcon>
          <ListItemText primary={translate("sellerCentralView")} />
        </MenuItem>

        {row.orderFlag ? (
          <MenuItem
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setRemoveDialogOpen(true);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <FlagIcon />
            </ListItemIcon>
            <ListItemText primary={translate("flag.unassign")} />
          </MenuItem>
        ) : (
          <MenuItem
            onClick={() => {
              setAssignDialogOpen(true);
              handleMenuClose();
            }}
          >
            <ListItemIcon>
              <FlagIcon />
            </ListItemIcon>
            <ListItemText primary={translate("flag.assign")} />
          </MenuItem>
        )}
      </Menu>

      <AmazonViewDialog
        open={amazonViewDialogOpen}
        onClose={() => setAmazonViewDialogOpen(false)}
        orderItems={orderItemsWithImages}
      />

      {row.orderFlag ? (
        <UnassignFlag
          open={removeDialogOpen}
          onClose={() => setRemoveDialogOpen(false)}
          flag={row.orderFlag}
          orderId={row.id}
        />
      ) : (
        <AssignFlag
          open={assignDialogOpen}
          onClose={() => setAssignDialogOpen(false)}
          orderId={row.id}
        />
      )}
    </>
  );
};
